/**
 * Merchant Lead Service
 *
 * Solicitudes de integración (lead capture) — espejo de
 * `src/modules/merchant-lead` del backend. Gestionadas por el equipo de
 * soporte.
 */

import { apiClient } from './api.service';
import type {
  MerchantLead,
  MerchantLeadQueryParams,
  MerchantLeadPaginatedResponse,
  AssignMerchantLeadDto,
  UpdateMerchantLeadStatusDto,
  AddMerchantLeadNoteDto,
} from '@/types/merchant-lead.types';

class MerchantLeadService {
  private readonly basePath = '/leads';

  async getAll(
    params: MerchantLeadQueryParams = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<MerchantLeadPaginatedResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.set('page', String(params.page));
    if (params.limit) sp.set('limit', String(params.limit));
    if (params.status) sp.set('status', params.status);
    if (params.search) sp.set('search', params.search);
    if (params.assignedToStaffId)
      sp.set('assignedToStaffId', params.assignedToStaffId);
    if (params.pendingOnly) sp.set('pendingOnly', 'true');

    const query = sp.toString();
    const endpoint = query ? `${this.basePath}?${query}` : this.basePath;

    const response = await apiClient.get<MerchantLeadPaginatedResponse>(
      endpoint,
      { signal: opts.signal },
    );
    return response.data;
  }

  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<MerchantLead> {
    const response = await apiClient.get<MerchantLead>(
      `${this.basePath}/${id}`,
      { signal: opts.signal },
    );
    return response.data;
  }

  async assign(id: string, dto: AssignMerchantLeadDto): Promise<MerchantLead> {
    const response = await apiClient.patch<MerchantLead>(
      `${this.basePath}/${id}/assign`,
      dto,
    );
    return response.data;
  }

  async updateStatus(
    id: string,
    dto: UpdateMerchantLeadStatusDto,
  ): Promise<MerchantLead> {
    const response = await apiClient.patch<MerchantLead>(
      `${this.basePath}/${id}/status`,
      dto,
    );
    return response.data;
  }

  async addNote(
    id: string,
    dto: AddMerchantLeadNoteDto,
  ): Promise<MerchantLead> {
    const response = await apiClient.post<MerchantLead>(
      `${this.basePath}/${id}/notes`,
      dto,
    );
    return response.data;
  }

  async convert(id: string, companyId: string): Promise<MerchantLead> {
    const response = await apiClient.post<MerchantLead>(
      `${this.basePath}/${id}/convert`,
      { companyId },
    );
    return response.data;
  }
}

export const merchantLeadService = new MerchantLeadService();
